import * as React from 'react';
import {useSelector} from '@xstate/react';
import {zodResolver} from '@hookform/resolvers/zod';
import {FormProvider, useForm} from 'react-hook-form';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {
  BodyMedium,
  SubTitleMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Modal} from '@spaced-out/ui-design-system/lib/components/Modal';
import {SemanticIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import {
  selectBookingFormInputs,
  selectAdditionalQuestions,
} from '../../selectors/booking-selector';
import {FormInput} from '../../lib/form-input';
import {getDefaultFormValues} from '../../utils/form';
import {useRootMachine} from '../../hooks/useRootMachine';
import {selectSession} from '../../selectors/session-selector';
import {BrandButton} from '../../lib/brand-button/brand-button';
import {getFormSchema} from '../booking-form/booking-form.schema';
import {useBookingMachine} from '../../hooks/useBookingMachine';
import {selectCountryCode, selectError} from '../../selectors/commonSelector';

import css from './request-more-availability-form.css';


export const RequestMoreAvailabilityForm = () => {
  const {t, language} = useLabelTranslation();

  // machines
  const {rootActor} = useRootMachine();
  const {bookingActor, bookingState} = useBookingMachine();

  // selectors
  const {errorMessage, allowRetry} = useSelector(bookingActor, (state) =>
    selectError(state, t),
  );
  const session = useSelector(rootActor, (state) => selectSession(state));
  const formInputs = useSelector(bookingActor, (state) =>
    selectBookingFormInputs(state, t),
  );
  const countyCode = useSelector(rootActor, (state) =>
    selectCountryCode(state),
  );
  const additionalQuestions = useSelector(bookingActor, (state) =>
    selectAdditionalQuestions(state),
  );

  // form
  const formSchema = getFormSchema({items: formInputs, countyCode, t});
  const {defaultValues, disabledFields} = getDefaultFormValues(
    formInputs,
    session,
  );

  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);

  const methods = useForm({
    defaultValues,
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
  });

  // recreate form when language changes because React Hook Form caches error messages in its internal state
  React.useEffect(() => {
    methods.reset();
  }, [language, methods]);

  const onSubmit = () => {
    const formValues = methods.getValues();
    const {guestName, guestEmail, guestPhone, ...restQuestions} = formValues;

    const updatedAdditionalQuestionAnswers = Object.entries(restQuestions).map(
      ([_key, value], index) => ({
        question: additionalQuestions[index]?.question || '',
        answer: value as string,
      }),
    );

    bookingActor.send({
      type: 'form.submit',
      guestName: guestName as string,
      guestEmail: guestEmail as string,
      guestPhone: guestPhone as string,
      additionalQuestionAnswers: updatedAdditionalQuestionAnswers,
    });
  };

  const handleFormSubmit = methods.handleSubmit(() => {
    if (session?.id) {
      setIsConfirmModalOpen(true);
    } else {
      onSubmit();
    }
  });

  const handleConfirmSubmit = () => {
    setIsConfirmModalOpen(false);
    methods.handleSubmit(onSubmit)();
  };

  const handleBack = () => {
    bookingActor.send({type: 'requestMoreAvailabilityForm.back'});
  };

  const isSubmitting = bookingState.matches({
    requestMoreAvailabilityForm: 'submitting',
  });
  const hasError = bookingState.matches({
    requestMoreAvailabilityForm: 'retrySubmit',
  });

  return (
    <>
      <FormProvider {...methods}>
        <form className={css.formContainer} onSubmit={handleFormSubmit}>
          <div className={css.formContent}>
            <div className={css.questionContainer}>
              <SubTitleMedium>
                {t(
                  'Please_answer_the_following_questions',
                  'Please answer the following questions',
                )}
              </SubTitleMedium>
              {formInputs.map((item) => (
                <FormInput
                  key={item.name}
                  item={item}
                  disabled={disabledFields[item.name]}
                />
              ))}
            </div>
          </div>
          <div className={css.submitButtonSection}>
            <div className={css.buttonContainer}>
              <Button
                type="tertiary"
                size="medium"
                iconLeftName="chevron-left"
                onClick={handleBack}
                disabled={isSubmitting}
              >
                {t('Back', 'Back')}
              </Button>
              <BrandButton
                actionType="submit"
                size="medium"
                disabled={hasError && !allowRetry}
                isLoading={isSubmitting}
              >
                {t('Submit', 'Submit')}
              </BrandButton>
            </div>
            {hasError && (
              <InContextAlert
                leftIconName="shield-exclamation"
                semantic="danger"
              >
                {/* already translated in errorMessage */}
                {/* eslint-disable-next-line @spaced-out/i18n/no-dynamic-labels */}
                {errorMessage}
              </InContextAlert>
            )}
          </div>
        </form>
      </FormProvider>
      <ConfirmProceedModal
        isOpen={isConfirmModalOpen}
        hasError={hasError}
        isSubmitting={isSubmitting}
        onConfirm={handleConfirmSubmit}
        onClose={() => setIsConfirmModalOpen(false)}
      />
    </>
  );
};

type ConfirmProceedModalProps = {
  isOpen: boolean;
  hasError: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const ConfirmProceedModal = ({
  isOpen,
  hasError,
  isSubmitting,
  onClose,
  onConfirm,
}: ConfirmProceedModalProps) => {
  const {t} = useLabelTranslation();
  return (
    <Modal
      size="small"
      tapOutsideToClose
      isOpen={isOpen}
      onClose={onClose}
      classNames={{content: css.confirmModal}}
    >
      <div className={css.confirmModalIcon}>
        <SemanticIcon
          type="solid"
          size="small"
          name="warning"
          semantic="warning"
        />
      </div>
      <div className={css.confirmModalContent}>
        <SubTitleMedium className={css.confirmModalTitle}>
          {t('Confirm_Proceed', 'Confirm Proceed?')}
        </SubTitleMedium>
        <BodyMedium color="secondary" className={css.confirmModalDescription}>
          {t(
            'Are_you_sure_you_want_to_proceed',
            'Are you sure you want to proceed?',
          )}
        </BodyMedium>
      </div>
      <div className={css.confirmModalActions}>
        <Button
          size="medium"
          type="tertiary"
          disabled={isSubmitting}
          onClick={onClose}
        >
          {t('Close', 'Close')}
        </Button>
        <Button
          size="medium"
          type="primary"
          isLoading={isSubmitting}
          onClick={onConfirm}
          disabled={hasError}
        >
          {t('Confirm', 'Confirm')}
        </Button>
      </div>
    </Modal>
  );
};
